import gql from 'graphql-tag';

import { PRODUCT_FOR_ADMIN_FRAGMENT, PURCHASED_PRODUCT_FRAGMENT } from './Product';
import { PUBLIC_TEAM_FRAGMENT, TEAM_DOMAIN_SETTINGS_FRAGMENT, TEAM_FRAGMENT } from './TeamFragment';
import { TEAM_MEMBER_FRAGMENT } from './Users';

export const TEAM_AND_USERS_FRAGMENT = gql`
  fragment TeamAndUsers on Team {
    ...Team
    users {
      ...Member
    }
  }
  ${TEAM_FRAGMENT}
  ${TEAM_MEMBER_FRAGMENT}
`;

export const TEAM_FOR_ADMIN_FRAGMENT = gql`
  fragment TeamForAdmin on Team {
    ...Team
    subscriptionStatus
    subscriptionSubstatus
    billingCycle
    last4
    ccType
    plan
    nextPaymentDueDate
    credit
    brandingPromoterId
    primaryBillingContact
    billingContacts
    pendingSetupIntent
    purchasedProducts {
      ...PurchasedProduct
    }
  }
  ${TEAM_FRAGMENT}
  ${PURCHASED_PRODUCT_FRAGMENT}
`;

export const TEAM_FOR_ADMIN_NO_PENDING_SETUP_INTENT_FRAGMENT = gql`
  fragment TeamForAdminNoPendingSetupIntent on Team {
    ...Team
    subscriptionStatus
    subscriptionSubstatus
    billingCycle
    last4
    ccType
    plan
    nextPaymentDueDate
    credit
    primaryBillingContact
    billingContacts
    purchasedProducts {
      ...PurchasedProduct
    }
  }
  ${TEAM_FRAGMENT}
  ${PURCHASED_PRODUCT_FRAGMENT}
`;

export const GET_MY_TEAM = gql`
  query myTeam {
    myTeam {
      ...TeamAndUsers
    }
  }
  ${TEAM_AND_USERS_FRAGMENT}
`;

export const GET_TEAM_TAGS = gql`
  query teamTags {
    teamTags
  }
`;

export const GET_MY_ADMIN_TEAM = gql`
  query myTeamForAdmin {
    myTeam {
      ...TeamForAdmin
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const PERMANENTLY_DELETE_TEAM = gql`
  mutation permanentlyDeleteTeam($id: String!) {
    permanentlyDeleteTeam(id: $id)
  }
`;

export const PERMANENTLY_DELETE_USER = gql`
  mutation permanentlyDeleteUser($id: String!, $replacementId: String!, $teamId: String!) {
    permanentlyDeleteUser(id: $id, replacementId: $replacementId, teamId: $teamId) {
      ...TeamAndUsers
    }
  }
  ${TEAM_FRAGMENT}
`;

export const REMOVE_USER = gql`
  mutation removeUser($id: String!, $replacementId: String!) {
    removeUser(id: $id, replacementId: $replacementId) {
      ...TeamAndUsers
    }
  }
  ${TEAM_FRAGMENT}
`;

export const UPDATE_USER = gql`
  mutation updateUser($data: EditUserInput!) {
    updateUser(data: $data) {
      ...Member
    }
  }
  ${TEAM_MEMBER_FRAGMENT}
`;

export const ADD_USER = gql`
  mutation addUser($data: NewUserInput!) {
    addUser(data: $data) {
      ...Member
    }
  }
  ${TEAM_MEMBER_FRAGMENT}
`;

export const UPDATE_BILLING_CONTACTS = gql`
  mutation updateBillingContacts($data: BillingContactInput!) {
    updateBillingContacts(data: $data) {
      ...TeamForAdmin
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const UPDATE_REWARDS_CONTACT = gql`
  mutation updateRewardsContact($userId: String!) {
    updateRewardsContact(userId: $userId) {
      ...TeamForAdmin
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const UPDATE_BILLING_INFO = gql`
  mutation updateBillingInfo($data: UpdateBillingInfoInput!) {
    updateBillingInfo(data: $data) {
      ...TeamForAdminNoPendingSetupIntent
    }
  }
  ${TEAM_FOR_ADMIN_NO_PENDING_SETUP_INTENT_FRAGMENT}
`;

export const CHARGE_PENDING_INVOICES = gql`
  mutation chargePendingInvoices {
    chargePendingInvoices {
      paymentIntentSecret
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation cancelSubscription($data: CancelReasonInput!) {
    cancelSubscription(data: $data) {
      ...TeamForAdmin
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const RESUBSCRIBE_SUBSCRIPTION = gql`
  mutation resubscribeSubscription {
    resubscribeSubscription {
      ...TeamForAdmin
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const RESTORE_SUBSCRIPTION = gql`
  mutation restoreSubscription(
    $fresh: Boolean!
    $data: UpdateBillingInfoInput
    $paymentIntentId: String
  ) {
    restoreSubscription(fresh: $fresh, data: $data, paymentIntentId: $paymentIntentId) {
      paymentIntentSecret
      team {
        ...TeamForAdmin
      }
    }
  }
  ${TEAM_FOR_ADMIN_FRAGMENT}
`;

export const TEAM_FOR_DOMAIN = gql`
  query teamForDomain($domain: String!) {
    teamForDomain(domain: $domain) {
      redirectUrl
      team {
        ...PublicTeam
      }
    }
  }
  ${PUBLIC_TEAM_FRAGMENT}
`;

export const TEAM_FROM_ID = gql`
  query teamFromId($id: String!) {
    team(id: $id) {
      ...PublicTeam
    }
  }
  ${PUBLIC_TEAM_FRAGMENT}
`;

export const TEAM_USERS = gql`
  query teamUsers($filters: UserFilters!) {
    teamUsers(filters: $filters) {
      ...Member
    }
  }
  ${TEAM_MEMBER_FRAGMENT}
`;

export const RESEND_INVITATION_EMAIL = gql`
  mutation resendInvitationEmail($userId: String!, $teamId: String!) {
    resendInvitationEmail(userId: $userId, teamId: $teamId)
  }
`;

export const CURRENT_BILLING_INFO = gql`
  fragment CardInfo on CardInfo {
    brand
    exp_month
    exp_year
    last4
  }
  query currentBillingInfo {
    currentBillingInfo {
      firstName
      lastName
      address1
      address2
      city
      country
      province
      postal
      card {
        ...CardInfo
      }
    }
  }
`;

export const UPDATE_TEAM_SETTINGS = gql`
  mutation updateTeamSettings($data: TeamInput!) {
    updateTeamSettings(data: $data) {
      ...Team
    }
  }
  ${TEAM_FRAGMENT}
`;

export const UPDATE_SUBDOMAIN = gql`
  mutation updateSubdomain($subdomain: String!) {
    updateSubdomain(subdomain: $subdomain) {
      id
    }
  }
`;

export const UPDATE_DOMAIN = gql`
  mutation updateDomain($domain: String!) {
    updateDomain(domain: $domain) {
      id
    }
  }
`;

export const VERIFY_DOMAIN = gql`
  mutation verifyDomain {
    verifyDomain {
      id
    }
  }
`;

export const GET_TEAM_DOMAIN_SETTINGS = gql`
  query getTeamDomainSettings {
    me {
      id
      team {
        ...TeamDomainSettings
      }
    }
  }
  ${TEAM_DOMAIN_SETTINGS_FRAGMENT}
`;

export const GET_FIELD_PROPERTIES = gql`
  query fieldProperties {
    fieldProperties
  }
`;

export const GET_SUBSCRIPTION_INFO = gql`
  fragment SubscriptionInfo on SubscriptionInfo {
    status
    subStatus
    credit
    currency
    activeWebinars
    endedDate
    nextPaymentDueDate
    trialEndingDate
    amountOwed
    billingCycle
    currentPeriodBillingCycle
    products {
      ...ProductForAdmin
    }
  }
  query subscriptionInfo {
    subscriptionInfo {
      ...SubscriptionInfo
    }
  }
  ${PRODUCT_FOR_ADMIN_FRAGMENT}
`;

export const CONFIRM_PENDING_SETUP_INTENT = gql`
  mutation confirmPendingSetupIntent {
    confirmPendingSetupIntent {
      require3ds
      clientSecret
    }
  }
`;

export const VERIFY_PENDING_SETUP_INTENT = gql`
  mutation verifyPendingSetupIntent {
    verifyPendingSetupIntent {
      id
      pendingSetupIntent
    }
  }
`;
